<script setup lang="ts">
  import { provide, ref, computed, onBeforeMount, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { RouterView } from 'vue-router'
  import { useFeaturesStore } from '@/shared/store/featuresStore'
  import { useUserStore } from '@/shared/store'
  import { captureMessage } from '@sentry/vue'
  import { useWebView } from '@qicgroupdoha/qic-use'
  import { useGoogleAnalytics } from '@/shared/composables'

  const { fetchRemoteConfig } = useFeaturesStore()
  const userStore = useUserStore()
  const { locale } = useI18n()
  const { isWebView } = useWebView()
  const gaService = useGoogleAnalytics()

  const isReady = ref(false)

  const isRTL = computed(() => locale.value === 'ar')

  const prefillUserData = async () => {
    const personalAccountString = sessionStorage.getItem('personal-account-login')
    if (!personalAccountString) return

    const personalData = JSON.parse(personalAccountString)
    if (
      personalData?.access_token &&
      personalData?.csrf_token &&
      personalData?.source_id &&
      personalData?.language
    ) {
      await userStore.prefillUserDataByAuth(
        personalData.access_token,
        personalData.csrf_token,
        personalData.source_id,
        personalData.language
      )
    }
  }

  Promise.allSettled([fetchRemoteConfig(), prefillUserData()]).finally(() => {
    isReady.value = true
  })

  const sendWebViewApp = () => {
    if (!isWebView()) return

    const googleId = localStorage.getItem('GoogleID')
    if (googleId) {
      gaService.sendWebViewApp(googleId)
    }
  }

  provide('isRTL', isRTL)
  // this is used by the loyalty-components lib to send errors to sentry correctly
  provide('captureMessage', captureMessage)

  watch(
    locale,
    (newLocale) => {
      const root = document.querySelector(':root') as HTMLElement
      if (!newLocale || !root) return
      const dir = newLocale === 'ar' ? 'rtl' : 'ltr'
      root.style.setProperty('--dp-direction', dir)
      root.setAttribute('dir', dir)
    },
    { immediate: true }
  )

  onBeforeMount(() => {
    sendWebViewApp()
  })
</script>

<template>
  <RouterView v-if="isReady" :class="{ rtl: isRTL }" />
</template>
